import { formatLargeNumber } from 'app/shared/utilities/text_utils';
import { COVERAGE_PLAN_TYPE } from 'app/views/view_types/network/features/rf_planning/collections/rf_plan_coverage_types';
import UtilityService from 'app/shared/utilities/utility.service';
import Constant from 'app/shared/utilities/constant';
import { ConfigService } from 'app/config/config.service';

export interface RFPlanCoverageMapGenerationResult {
    rfPlanName: string;
    rfPlanId: number;
    ignoredTransmitters: Array<IgnoredTransmitter>;
    totalTransmitterCount: number;
    areaOfRFPlanSquareMeters: number;
    generationTimeMs: number;
    generationAbsTime: number;
    coverageType: COVERAGE_PLAN_TYPE;
    resolutionLimited: boolean;
    resolutionX: number;
    resolutionY: number;
    dimX: number;
    dimY: number;
}

export enum IgnoredTransmitterReason {
    NO_ASSET_LOCATION,
    NO_RADIO_POWER,
    NO_ANT_FILE_FOUND,
}

export interface IgnoredTransmitter {
    id: number;
    name: string;
    reason: IgnoredTransmitterReason;
}

export function formatHTMLRFPlanCoverageMapGenerationResult(
    result: RFPlanCoverageMapGenerationResult,
): string {
    const ignoredForPower = !!result.ignoredTransmitters.find(
        (i) => i.reason === IgnoredTransmitterReason.NO_RADIO_POWER,
    );
    const ignoredForLocation = !!result.ignoredTransmitters.find(
        (i) => i.reason === IgnoredTransmitterReason.NO_ASSET_LOCATION,
    );
    const ignoredForAntFile = !!result.ignoredTransmitters.find(
        (i) => i.reason === IgnoredTransmitterReason.NO_ANT_FILE_FOUND,
    );
    const plural = result.ignoredTransmitters.length !== 1;
    const pronoun = plural ? 'they' : 'it';
    const reasonString = ignoredForPower
        ? ignoredForLocation
            ? ignoredForAntFile
                ? `because ${pronoun} had no location, no power and frequency, or no ant file could be found`
                : `because ${pronoun} had no location, or no power and frequency`
            : ignoredForAntFile
              ? `because ${pronoun} had no power and frequency, or no ant file could be found`
              : `because ${pronoun} had no power and frequency`
        : ignoredForLocation
          ? ignoredForAntFile
              ? `because ${pronoun} had no location, or no ant file could be found`
              : `because ${pronoun} had no location`
          : ignoredForAntFile
            ? `because no ant file${plural ? 's' : ''} could be found`
            : 'for no reason';

    const ignoredTransmitterString = !result.ignoredTransmitters.length
        ? ''
        : `Ignored ${result.ignoredTransmitters.length} of ${result.totalTransmitterCount} RF Transmitters ${reasonString}`;

    const { value, unit } = UtilityService.normalizeUnit(
        Constant.UNITS.TIME,
        result.generationTimeMs,
        'millisecond',
    );
    const timeTaken = `${value} ${unit}s`;
    let resolutionComponent = `<p>Resolution: ${result.resolutionX} x ${result.resolutionY}</p>`;
    if (result.resolutionLimited) {
        resolutionComponent = `<p>Resolution exceeded the maximum of
        ${ConfigService.rfPlanMaxResolution} x ${
            ConfigService.rfPlanMaxResolution
        }, and was lowered to
        ${result.resolutionX} x ${result.resolutionY}, (${(
            result.resolutionX /
            (result.dimX / 1000)
        ).toFixed(2)}
        pixels per km). Please try lowering the resolution of the RF Plan, or increasing the RSSI cutoff of the transmitters.</p>`;
    }

    return `
        <p>Coverage for ${result.rfPlanName} generated in ${timeTaken}</p>
        <p>Area: ${formatLargeNumber(result.areaOfRFPlanSquareMeters, 0, '.', ' ')}m²</p>
        ${resolutionComponent}
        ${ignoredTransmitterString}
    `;
}
